import ArrowRightAltIcon from '@mui/icons-material/ArrowRightAlt';
import { Stack, Typography } from '@mui/material';
import { ProductGroups } from 'enums';
import { QualificationPromptPlaceholders } from 'features/co-pilot/campaign-builder/campaign-builder.types';
import getLocalizedText from 'features/co-pilot/translationFileProduct';
import PaiHelpIcon from 'features/ui/base-components/pai-help-icon';
import PaiButton from 'features/ui/base-components/pai-button';
import PaiCheckbox from 'features/ui/base-components/pai-checkbox';
import PaiTextArea from 'features/ui/base-components/pai-text-area'; // <-- For LeadBooster text area
import _ from 'lodash';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { makeStyles } from 'tss-react/mui';
import AiContactQualifierDetails from './ai-contact-qualifier-details';

interface Props {
  contactQualification: {
    loading: boolean;
    promptPlaceholders: QualificationPromptPlaceholders;
    selectedOptions: string[];
    rejudgeExistingContacts: boolean;
    prompt: string;
    hasOldKeys: boolean;
    isJudging: boolean;
  };
  productGroup: ProductGroups;

  handleAiQualifierDetailsChange(type: keyof QualificationPromptPlaceholders): (event: React.ChangeEvent<HTMLInputElement>) => void;
  handleAiQualifierPromptChange(event: React.ChangeEvent<HTMLInputElement>): void;

  handleAiQualifierSelectedOptionChange(event: React.ChangeEvent<HTMLInputElement>): void;
  handleRejudgeExistingContactsChange(event: React.ChangeEvent<HTMLInputElement>): void;
  handleIsJudgingChange(event: React.ChangeEvent<HTMLInputElement>): void;

  updateQualifyingContactsRecruiting(): void;

  updateQualifyingContactsLead(): void;
}

const ContactQualification: React.FC<Props> = ({
  contactQualification,
  productGroup,
  handleAiQualifierDetailsChange,
  handleAiQualifierPromptChange,
  handleAiQualifierSelectedOptionChange,
  handleRejudgeExistingContactsChange,
  handleIsJudgingChange,
  updateQualifyingContactsRecruiting,
  updateQualifyingContactsLead,
}) => {
  const { classes, cx } = useStyles();
  const { t } = useTranslation();

  const { loading, promptPlaceholders, prompt, selectedOptions, rejudgeExistingContacts, hasOldKeys, isJudging } = contactQualification;

  const checkboxOptions = [
    { label: getLocalizedText('topCandidates', productGroup, t), value: 'A' },
    { label: getLocalizedText('strongContenders', productGroup, t), value: 'B' },
    { label: getLocalizedText('backupCandidatePool', productGroup, t), value: 'C' },
  ];

  return (
    <Stack className={classes.contactQualification} gap={2}>
      <section className={classes.section}>
        <div className={classes.titleContainer}>
          <Typography className={classes.blueTitle}>{getLocalizedText('aiContactQualification', productGroup, t)}</Typography>
          <Typography className={classes.title}>{getLocalizedText('aiContactQualificationDescription', productGroup, t)}</Typography>
          <Typography className={classes.greyText}>{getLocalizedText('aiContactQualificationExplanation', productGroup, t)}</Typography>
        </div>
      </section>

      <section className={classes.section}>
        <ArrowRightAltIcon className={cx(classes.arrow, 'active')} />
        <Stack direction={'column'} gap={2} mb={'16px'}>
          <PaiCheckbox checked={isJudging} label={t('enableJudgingForThisCampaign')} onChange={handleIsJudgingChange} />
          <Typography className={classes.greyText} style={{ fontSize: '14px', marginTop: '-8px' }}>
            {t('enableJudgingForThisCampaignDescription')}
          </Typography>
        </Stack>

        <Typography className={classes.subTitle} mb={'8px'}>
          {getLocalizedText('provideKeyCharacteristicsTitle', productGroup, t)}
        </Typography>
        <Typography className={classes.greyText} mb={'16px'}>
          {getLocalizedText('provideKeyCharacteristicsDescription', productGroup, t)}
        </Typography>

        {productGroup === ProductGroups.HR_BOOSTER && isJudging ? (
          <AiContactQualifierDetails
            promptPlaceholders={promptPlaceholders}
            hasOldKeys={hasOldKeys}
            handleAiQualifierDetailsChange={handleAiQualifierDetailsChange}
          />
        ) : null}

        {productGroup === ProductGroups.LEAD_BOOSTER && isJudging ? (
          <div>
            <Stack direction={'row'} alignItems={'center'} gap={1} sx={{ marginLeft: '4px', marginBottom: '4px' }}>
              <Typography className={classes.label}>{`${t('leadBoosterIcpTitle')} *`}</Typography>
              <PaiHelpIcon tooltipText={t('leadBoosterIcpExplanation')} />
            </Stack>
            <PaiTextArea
              rows={7}
              boldLabel={true}
              placeholder={t('leadBoosterIcpPlaceholder')}
              variant={'white'}
              value={prompt}
              onChange={handleAiQualifierPromptChange}
            />
          </div>
        ) : null}
      </section>

      <section className={classes.section}>
        <ArrowRightAltIcon className={cx(classes.arrow, 'active')} />
        <Typography className={classes.subTitle} mb={'8px'}>
          {getLocalizedText('contactsToAddTitle', productGroup, t)}
        </Typography>
        <Typography className={classes.greyText} mb={'16px'}>
          {getLocalizedText('contactsToAddDescription', productGroup, t)}
        </Typography>

        <Stack gap={1}>
          {checkboxOptions.map((option) => (
            <PaiCheckbox
              key={option.value}
              value={option.value}
              label={option.label}
              checked={_.includes(selectedOptions, option.value)}
              onChange={handleAiQualifierSelectedOptionChange}
            />
          ))}
        </Stack>
      </section>

      <section className={cx(classes.section, 'border-bottom-disabled')}>
        <ArrowRightAltIcon className={cx(classes.arrow, 'active')} />
        <Typography className={classes.subTitle} mb={'8px'}>
          {getLocalizedText('reviewContactsTitle', productGroup, t)}
        </Typography>
        <Typography className={classes.greyText} mb={'16px'}>
          {getLocalizedText('reviewContactsDescription', productGroup, t)}
        </Typography>

        <PaiCheckbox
          label={getLocalizedText('redoQualification', productGroup, t)}
          checked={rejudgeExistingContacts}
          onChange={handleRejudgeExistingContactsChange}
        />
      </section>

      <section className={cx(classes.section, 'last-section')}>
        <ArrowRightAltIcon className={classes.arrow} />
        <Typography className={classes.title}>{getLocalizedText('startQualificationProcess', productGroup, t)}</Typography>
        <Typography className={classes.greyText} mb={'24px'}>
          {getLocalizedText('startQualificationProcessDescription', productGroup, t)}
        </Typography>

        <PaiButton
          text={getLocalizedText('updateQualificationCriteria', productGroup, t)}
          border={'rounded'}
          action={productGroup === ProductGroups.HR_BOOSTER ? updateQualifyingContactsRecruiting : updateQualifyingContactsLead}
          disabled={!isJudging || _.isEmpty(selectedOptions)}
          loading={loading}
        />
      </section>
    </Stack>
  );
};

const useStyles = makeStyles()((theme) => ({
  contactQualification: {
    padding: '0 24px',
  },
  titleContainer: {
    flexBasis: '50%',
  },
  blueTitle: {
    fontSize: theme.typography.pxToRem(15),
    color: theme.palette.primary.main,
    fontWeight: 'bold',
    marginBottom: '8px',
  },
  section: {
    position: 'relative',
    padding: '24px 48px',
    borderBottom: `1px solid ${theme.palette.greys.lighter}`,
    '&.border-bottom-disabled': {
      borderBottom: 'none',
    },
    '&.last-section': {
      margin: '0 -48px',
      padding: '24px calc(48px + 48px)',
      borderTop: `1px solid ${theme.palette.greys.lighter}`,
      borderBottom: 'none',
    },
  },
  arrow: {
    display: 'none',
    position: 'absolute',
    top: '18px',
    left: '0px',
    fontSize: theme.typography.pxToRem(32),
    color: theme.palette.primary.main,
    '&.active': {
      display: 'inline-block',
    },
  },
  title: {
    fontSize: theme.typography.pxToRem(20),
    fontWeight: 'bold',
    marginBottom: '8px',
  },
  subTitle: {
    fontSize: theme.typography.pxToRem(15),
    fontWeight: 'bold',
  },
  greyText: {
    fontSize: theme.typography.pxToRem(15),
    color: theme.palette.greys.medium,
  },
  label: {
    fontSize: theme.typography.pxToRem(14),
    fontWeight: 'bold',
  },
}));

export default ContactQualification;
